import { useLazyQuery } from '@apollo/client';

import { Skeleton } from '@/shadcn/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { BUILD_INFO_QUERY, type BuildInfo } from '@/gql/rest/buildInfoQuery';

type Props = {
  env: string;
  allowedEnvs: readonly string[];
  className?: string;
};

const linkClass =
  'break-words text-xs underline underline-offset-2 hover:opacity-80';
const labelClass =
  'font-semibold text-[9px] uppercase tracking-wide opacity-60';

// A value that links to GitLab when a repoUrl is available, plain text otherwise.
const Field = ({
  label,
  value,
  href
}: {
  label: string;
  value: string;
  href: string | null;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className={labelClass}>{label}</span>
    {href ? (
      <a
        className={linkClass}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {value}
      </a>
    ) : (
      <span className="break-words text-xs">{value}</span>
    )}
  </div>
);

const BuildInfoContent = ({ build }: { build: BuildInfo }) => {
  const { sha, ref, title, author, repoUrl } = build;
  const commit = [sha, title].filter(Boolean).join(' · ');

  if (!(ref || commit || author)) {
    return <span className="text-xs opacity-60">No build info available</span>;
  }

  return (
    <>
      {ref && (
        <Field
          href={repoUrl ? `${repoUrl}/-/tree/${encodeURIComponent(ref)}` : null}
          label="Branch"
          value={ref}
        />
      )}
      {commit && (
        <Field
          href={repoUrl && sha ? `${repoUrl}/-/commit/${sha}` : null}
          label="Commit"
          value={commit}
        />
      )}
      {author && <Field href={null} label="Author" value={author} />}
    </>
  );
};

const BuildInfoSkeleton = () => (
  <>
    {['branch', 'commit', 'author'].map(key => (
      <div className="flex flex-col gap-1" key={key}>
        <Skeleton className="h-2 w-12" />
        <Skeleton className="h-3 w-32" />
      </div>
    ))}
  </>
);

export const EnvPillWithBuildInfo = ({
  env,
  allowedEnvs,
  className
}: Props) => {
  const [fetchBuildInfo, { data, loading, error }] =
    useLazyQuery(BUILD_INFO_QUERY);

  const tooltipEnabled = allowedEnvs.includes(env);
  const pill = <span className={className}>{env}</span>;

  if (!tooltipEnabled) return pill;

  return (
    <TooltipProvider delayDuration={0}>
      {/* useLazyQuery is cached, so repeated hovers don't refetch. */}
      <Tooltip onOpenChange={open => open && fetchBuildInfo()}>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent
          className="flex min-w-52 max-w-xs flex-col gap-2 px-3 py-2"
          side="bottom"
        >
          {loading && <BuildInfoSkeleton />}
          {error && (
            <span className="text-xs opacity-60">
              Couldn&apos;t load build info
            </span>
          )}
          {data?.buildInfo && <BuildInfoContent build={data.buildInfo} />}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
